import ApiService, { HttpMethod } from "../../commons/api/api_service";
import BaseResponse from "../../commons/api/base_response";
import { Endpoints } from "../../commons/api/endpoints";
import { ProviderVM } from "../../commons/base/providers/view_model";
import {
    ShowErrorViewModel,
    ShowSnackbarModel,
} from "../../commons/scaffold/scaffold";
import OptionModel from "../models/option_model";
import { SoldOutCaseKindEnum } from "../models/sold_out_case_kind_enum";

const EQPOSTFIX = "EQ";

interface ItemToAffectedMenuModel {
    id: number;
    type: SoldOutCaseKindEnum;
    name: string;
    affectedMenuIds: number[];
}

export default class SoldOutCmsEditorViewModel extends ProviderVM {
    isLoading = false;
    errorModel?: ShowErrorViewModel;
    snackbarModel?: ShowSnackbarModel;
    isShowingModal: boolean = false;

    itemMap: Map<number, OptionModel> = new Map<number, OptionModel>();
    equipmentMap: Map<string, OptionModel> = new Map<string, OptionModel>();
    menuMap: Map<number, OptionModel> = new Map<number, OptionModel>();
    preparationMap: Map<number, OptionModel> = new Map<number, OptionModel>();
    itemToAffectedMenusMap: Map<number, ItemToAffectedMenuModel> = new Map<
        number,
        ItemToAffectedMenuModel
    >();
    equipmentToAffectedMenusMap: Map<number, ItemToAffectedMenuModel> = new Map<
        number,
        ItemToAffectedMenuModel
    >();
    searchQuery = "";
    deletedMenus: number[] = [];

    // form data
    selectedCaseId?: string;
    selectedCaseType?: SoldOutCaseKindEnum;
    selectedAffectedMenuIds: number[] = [];
    selectedNewMenuId?: number;
    isCaseOptionDisabled: boolean = true;
    isDeleteOptionDisabled: boolean = true;

    get filteredItemToAffectedMenus() {
        return [...this.itemToAffectedMenusMap.values(), ...this.equipmentToAffectedMenusMap.values()].filter((model) => {
            return model.name
                .toLowerCase()
                .includes(this.searchQuery.toLowerCase());
        });
    }

    get shownCaseOptions(): { label: string; value: number }[] {
        switch (this.selectedCaseType) {
            case SoldOutCaseKindEnum.EQUIPMENT:
                return [...this.equipmentMap.values()].map((model) => ({
                    label: model.title,
                    value: model.id,
                }));
            case SoldOutCaseKindEnum.ITEM:
                return [...this.itemMap.values()].map((model) => ({
                    label: model.title,
                    value: model.id,
                }));
            case SoldOutCaseKindEnum.PREPARATION:
                return [...this.preparationMap.values()].map((model) => ({
                    label: model.title,
                    value: model.id,
                }));
            default:
                return [];
        }
    }

    constructor() {
        super();
        this.loadPreloading();
        this.loadItemToAffectedMenuList();
    }

    loadPreloading() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(Endpoints.soldOutCmsPreloading, {
            httpMethod: HttpMethod.get,
            onSuccess: (
                response: BaseResponse<{
                    items: OptionModel[];
                    equipments: OptionModel[];
                    menus: OptionModel[];
                    preparations: OptionModel[];
                }>
            ) => {
                const itemMap = new Map<number, OptionModel>();
                const equipmentMap = new Map<string, OptionModel>();
                const menuMap = new Map<number, OptionModel>();
                const preparationMap = new Map<number, OptionModel>();

                response.data?.items.forEach((item) => {
                    itemMap.set(item.id, item);
                });

                response.data?.equipments.forEach((equipment) => {
                    equipmentMap.set(equipment.id+"eq", equipment);
                });

                response.data?.menus.forEach((menu) => {
                    menuMap.set(menu.id, menu);
                });

                response.data?.preparations.forEach((prep) => {
                    preparationMap.set(prep.id, prep);
                });

                this.emit(() => {
                    this.isLoading = false;
                    this.itemMap = itemMap;
                    this.equipmentMap = equipmentMap;
                    this.menuMap = menuMap;
                    this.preparationMap = preparationMap;
                });
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        message: `${error}`,
                        action: () => {},
                    };
                });
            },
        });
    }

    loadItemToAffectedMenuList() {
        this.emit(() => {
            this.isLoading = true;
        });

        ApiService.fetch(Endpoints.soldOutCmsItemToAffectedMenuList, {
            httpMethod: HttpMethod.get,
            onSuccess: (
                response: BaseResponse<{
                    mappings: ItemToAffectedMenuModel[];
                }>
            ) => {
                if (response.code === 200) {
                    const itemToAffectedMenusMap = new Map<
                        number,
                        ItemToAffectedMenuModel
                    >();
                    const equipmentToAffectedMenusMap = new Map<
                        number,
                        ItemToAffectedMenuModel
                    >

                    response.data?.mappings.forEach((model) => {
                        if (model.type == SoldOutCaseKindEnum.ITEM || model.type == SoldOutCaseKindEnum.PREPARATION) {
                            itemToAffectedMenusMap.set(model.id, model);
                        } else if (model.type == SoldOutCaseKindEnum.EQUIPMENT) {
                            equipmentToAffectedMenusMap.set(model.id, model);
                        }
                    });

                    this.emit(() => {
                        this.isLoading = false;
                        this.itemToAffectedMenusMap = itemToAffectedMenusMap;
                        this.equipmentToAffectedMenusMap = equipmentToAffectedMenusMap;
                    });
                } else {
                    this.emit(() => {
                        this.isLoading = false;
                        this.errorModel = {
                            message: `${response.message}`,
                            action: () => {},
                        };
                    });
                }
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        message: `${error}`,
                        action: () => {},
                    };
                });
            },
        });
    }

    submitData() {
        this.emit(() => {
            this.isLoading = true;
        });

        // console.log({
        //     id: this.selectedCaseId,
        //     type: this.selectedCaseType,
        //     affectedMenuIds: this.selectedAffectedMenuIds,
        // });

        let selectedCaseId: number;
        if (this.selectedCaseType == SoldOutCaseKindEnum.EQUIPMENT) {
            selectedCaseId = parseInt(this.selectedCaseId?.split(EQPOSTFIX)[0] ?? "0");
        } else {
            selectedCaseId = parseInt(this.selectedCaseId ?? "0")
        }

        ApiService.fetch(Endpoints.soldOutCmsSubmit, {
            httpMethod: HttpMethod.post,
            parameters: {
                id: selectedCaseId,
                type: this.selectedCaseType,
                affectedMenuIds: this.selectedAffectedMenuIds,
            },
            onSuccess: (
                response: BaseResponse<{
                    id: number;
                    type: SoldOutCaseKindEnum;
                    affectedMenuIds: number[];
                }>
            ) => {
                if (response.code === 200) {
                    this.emit(() => {
                        this.isLoading = false;
                        this.isShowingModal = false;
                        this.selectedCaseId = undefined;
                        this.selectedCaseType = undefined;
                        this.selectedAffectedMenuIds = [];
                        this.snackbarModel = {
                            key: Date.now().toString(),
                            type: "normal",
                            message: response.message,
                        };
                    });

                    this.loadItemToAffectedMenuList();
                } else {
                    this.emit(() => {
                        this.isLoading = false;
                        this.isShowingModal = false;
                        this.errorModel = {
                            message: response.message,
                            action: () => {},
                        };
                    });
                }
            },
            onFailure: (error) => {
                this.emit(() => {
                    this.isLoading = false;
                    this.errorModel = {
                        message: `${error}`,
                        action: () => {},
                    };
                });
            },
        });
    }

    openModalForCreate() {
        this.emit(() => {
            this.isShowingModal = true;
            this.selectedCaseId = undefined;
            this.selectedCaseType = undefined;
            this.selectedAffectedMenuIds = [];
            this.selectedNewMenuId = undefined;
            this.isCaseOptionDisabled = false;
            this.isDeleteOptionDisabled = true;
        });
    }

    openModalForUpdate(id: number) {
        const model = this.itemToAffectedMenusMap.get(id) 
            ?? this.equipmentToAffectedMenusMap.get(id);

        if (model) {
            let selectedCaseId: string | undefined;
            if (this.selectedCaseType == SoldOutCaseKindEnum.EQUIPMENT) {
                selectedCaseId = this.selectedCaseId?.split(EQPOSTFIX)[0];
            } else {
                selectedCaseId = this.selectedCaseId
            }
            this.emit(() => {
                this.selectedCaseId = selectedCaseId;
                this.selectedCaseType = model.type;
                this.selectedAffectedMenuIds = model.affectedMenuIds;
                this.selectedNewMenuId = undefined;
                this.isShowingModal = true;
                this.isCaseOptionDisabled = true;
                this.isDeleteOptionDisabled = true;
            });
        }
    }

    openModalForDelete(id: number) {
        const model = this.itemToAffectedMenusMap.get(id) 
            ?? this.equipmentToAffectedMenusMap.get(id);
        if (model) {
            let selectedCaseId: string | undefined;
            if (this.selectedCaseType == SoldOutCaseKindEnum.EQUIPMENT) {
                selectedCaseId = this.selectedCaseId?.split(EQPOSTFIX)[0];
            } else {
                selectedCaseId = this.selectedCaseId
            }
            this.emit(() => {
                this.selectedCaseId = selectedCaseId;
                this.selectedCaseType = model.type;
                this.selectedAffectedMenuIds = model.affectedMenuIds;
                this.selectedNewMenuId = undefined;
                this.isShowingModal = true;
                this.isCaseOptionDisabled = true;
                this.isDeleteOptionDisabled = false;
            });
        }
    }

    closeModal() {
        this.emit(() => {
            this.isShowingModal = false;
            this.selectedCaseId = undefined;
            this.selectedCaseType = undefined;
            this.selectedAffectedMenuIds = [];
            this.selectedNewMenuId = undefined;
            this.isCaseOptionDisabled = true;
            this.isDeleteOptionDisabled = true;
        });
    }

    updateSearchQuery(value: string) {
        this.emit(() => {
            this.searchQuery = value;
        });
    }

    updateSelectedCaseType(value: SoldOutCaseKindEnum) {
        this.emit(() => {
            this.selectedCaseType = value;
        });
    }

    updateSelectedCaseId(value: number) {
        let selectedCaseId: string | undefined;
        if (this.selectedCaseType == SoldOutCaseKindEnum.EQUIPMENT) {
            selectedCaseId = this.selectedCaseId?.split(EQPOSTFIX)[0];
        } else {
            selectedCaseId = this.selectedCaseId
        }
        this.emit(() => {
            this.selectedCaseId = selectedCaseId;
        });
    }

    updateSelectedNewMenuId(value: number) {
        this.emit(() => {
            this.selectedNewMenuId = value;
        });
    }

    handleAddNewSelectedMenuId(value: number) {
        if (!this.selectedAffectedMenuIds.includes(value)) {
            this.emit(() => {
                this.selectedAffectedMenuIds = [
                    ...this.selectedAffectedMenuIds,
                    value,
                ];
            });
        }
    }

    deleteSelectedMenuIds(selectedMenuIds: number[], caseId: number, caseType: SoldOutCaseKindEnum) {
        const payload = {
            id: caseId,
            type: caseType,
            menuIdToDelete: selectedMenuIds
        };
        ApiService.fetch(
            Endpoints.soldOutCmsDeleteMenus,
            {
                httpMethod: HttpMethod.post,
                parameters: payload,
                onSuccess: (response: BaseResponse<{
                    id: number,
                    type: SoldOutCaseKindEnum,
                    deletedMenus: number[]
                }>) => {
                    if (response.code == 200) {
                        const deletedMenus = response.data?.deletedMenus;
                        if (deletedMenus) {
                            // const model = this.itemToAffectedMenusMap.get(caseId);
                            // deletedMenus.forEach((menuId) => {
                            //     const i = model?.affectedMenuIds.indexOf(menuId);
                            //     if (model && i) {
                            //         model.affectedMenuIds = model?.affectedMenuIds.splice(i, 1) ?? []
                            //     };
                            // });
                            // if (model) {
                            //     this.emit(() => {
                            //         this.isLoading = false;
                            //         this.isDeleteOptionDisabled = true;
                            //         this.isShowingModal = false;
                            //         this.deletedMenus = deletedMenus ?? [];
                            //         this.selectedAffectedMenuIds = model.affectedMenuIds;
                            //     });
                            // }
                            window.location.reload();
                        }
                    }
                },
                onFailure: (error: any) => {
                    this.emit(() => {
                        this.isDeleteOptionDisabled = true;
                        this.isShowingModal = false;
                        this.isLoading = false;
                        this.errorModel = {
                            message: `${error}`,
                            action: () => {},
                        };
                    });
                }
            }   
        )

        this.emit(() => {
            this.selectedAffectedMenuIds = [];
        });
    }
}
